import { groupKey, groupLabel } from "@/utils/dateUtils";
import type { MailParticipant, MailSummary } from "./types";

const timeFormatter = new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit" });
const weekdayFormatter = new Intl.DateTimeFormat(undefined, { weekday: "short", hour: "2-digit", minute: "2-digit" });
const dayMonthFormatter = new Intl.DateTimeFormat(undefined, { day: "numeric", month: "short" });
const fullDateFormatter = new Intl.DateTimeFormat(undefined, { day: "numeric", month: "short", year: "numeric" });
const longDateTimeFormatter = new Intl.DateTimeFormat(undefined, {
  weekday: "short", day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
});

const DAY_MS = 24 * 60 * 60 * 1000;

const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

/**
 * Compact date for the message list, the way mail clients show it: the time for today,
 * the weekday for the last week, then the day and month, adding the year once it differs.
 */
export const formatListDate = (utc: string, now = new Date()): string => {
  const date = new Date(utc);
  const daysAgo = Math.round((startOfDay(now).getTime() - startOfDay(date).getTime()) / DAY_MS);

  if (daysAgo <= 0) return timeFormatter.format(date);
  if (daysAgo < 7) return weekdayFormatter.format(date);
  if (date.getFullYear() === now.getFullYear()) return dayMonthFormatter.format(date);
  return fullDateFormatter.format(date);
};

export const formatLongDate = (utc: string): string => longDateTimeFormatter.format(new Date(utc));

export type MailDateGroup = {
  label: string;
  items: MailSummary[];
};

/**
 * Splits a newest-first list into consecutive date groups, using the same periods as the
 * engagement feed: Today, Yesterday, Earlier this week, Last week, Earlier this month,
 * Last month, then one group per month.
 */
export const groupByDate = (items: MailSummary[]): MailDateGroup[] =>
  items.reduce<MailDateGroup[]>((groups, mail) => {
    const label = groupLabel(groupKey(mail.dateReceivedUtc));
    const current = groups[groups.length - 1];

    if (current?.label === label) {
      current.items.push(mail);
    } else {
      groups.push({ label, items: [mail] });
    }
    return groups;
  }, []);

/**
 * Up to two initials for an avatar. A contact's come from their first and last name, as on the
 * contact page, so a title such as "Mr" is never used; anyone else's from their name or address.
 */
export const initials = (participant: MailParticipant | null): string => {
  if (!participant) return "?";

  const nameInitials = [participant.firstName, participant.lastName]
    .map(part => part?.trim()[0])
    .filter(Boolean)
    .join("");
  if (nameInitials) return nameInitials.toUpperCase();

  const words = (participant.name?.trim() || participant.email?.split("@")[0] || "")
    .split(/[\s._-]+/)
    .filter(Boolean);

  if (words.length === 0) return "?";
  return (words[0][0] + (words.length > 1 ? words[words.length - 1][0] : "")).toUpperCase();
};

export const displayName = (participant: MailParticipant): string =>
  participant.name?.trim() || participant.email || "Unknown";

/**
 * The note shown after a name, matching what the legacy tab showed: whether a user is
 * inactive or deleted, a contact's company and status, or that the address is a company's.
 */
export const participantNote = (participant: MailParticipant): string | null => {
  switch (participant.type) {
    case "user":
      if (participant.status === "deleted") return "Deleted user";
      if (participant.status === "inactive") return "Inactive user";
      return "User";
    case "contact": {
      const status = participant.status === "inactive"
        ? "Inactive"
        : participant.status === "infoOnly" ? "Info only" : null;
      return [participant.companyName, status].filter(Boolean).join(", ") || null;
    }
    case "company":
      return "Company";
  }
};

export const subjectOrPlaceholder = (subject: string | null): string => subject?.trim() || "(No subject)";

const REPLY_PREFIX = /^\s*(re|fw|fwd|aw|sv|wg)\s*:\s*/i;

/** The subject a conversation is known by: without the RE: and FW: prefixes replies add. */
export const conversationTopic = (subject: string | null): string => {
  let topic = subject ?? "";
  while (REPLY_PREFIX.test(topic)) {
    topic = topic.replace(REPLY_PREFIX, "");
  }
  return subjectOrPlaceholder(topic);
};

const SNIPPET_LENGTH = 160;

/**
 * A one-line preview of a body. HTML is parsed into an inert document, which never runs scripts
 * or loads images, and only its text is used.
 */
export const plainTextSnippet = (body: string | null, isHtml: boolean): string => {
  if (!body) return "";

  const text = isHtml
    ? new DOMParser().parseFromString(body, "text/html").body.textContent ?? ""
    : body;

  const collapsed = text.replace(/\s+/g, " ").trim();
  return collapsed.length > SNIPPET_LENGTH ? `${collapsed.slice(0, SNIPPET_LENGTH)}…` : collapsed;
};

const joinNames = (participants: MailParticipant[]): string =>
  participants.map(displayName).join(", ");

/**
 * The name a list row leads with. Like a Sent Items folder, mail the user sent shows who it
 * went to; everything else shows who it came from.
 */
export const listCorrespondent = (mail: MailSummary): string => {
  if (mail.direction === "outbound" && mail.to.length > 0) {
    return `To: ${joinNames(mail.to)}`;
  }
  return mail.from ? displayName(mail.from) : "Unknown sender";
};

/** The other side of the conversation, shown under the subject in the list. */
export const listSecondaryLine = (mail: MailSummary): string | null => {
  if (mail.direction === "outbound") {
    return mail.from ? `From: ${displayName(mail.from)}` : null;
  }
  return mail.to.length > 0 ? `To: ${joinNames(mail.to)}` : null;
};
